import React, { useState, createContext, useContext } from 'react';
import { apiFetch } from '../../lib/supabase';
import { DEFAULT_PRICE_TABLE, getTableCols } from '../../config/constants';
import { useTenantConfig } from '../../hooks/useTenant';
import { F, C, btn, inp, lbl } from '../../config/styles';
import { uid, convertPrice, TWO_DIM_VARIANT_TYPE, isTwoDimVariant, deriveTitleNoColour, syncImagesFields, isVideoMedia } from '../../utils/products';
import { getFieldDefs } from '../../config/fields';
import { sanitizeHtml } from '../../utils/sanitize';
import { downloadProductImage, downloadProductImages } from '../../utils/imageImport';
import CropModal, { BulkCrop } from './CropModal';
import { croppedItem } from '../../utils/crop';
import { uploadMedia } from '../../utils/mediaUpload';

// Active collection title, provided by ProductEditor. Lets the AI description generator match
// brand voice on the collection (e.g. "UNIK: A Thousand Futures" → the "UNIK" brand voice).
export const CollectionNameContext = createContext('');

// ─────────────────────────────────────────────────────────────
// OPS FORM
// ─────────────────────────────────────────────────────────────

export function OpsForm({ p, onChange }) {
  const tenantConfig = useTenantConfig();
  const presets = tenantConfig?.variantPresets || [];
  const disabled = tenantConfig?.disabledFields || [];
  const productFields = deptProductFields(tenantConfig, 'ops'); // Base SKU + any custom ops fields
  // Configurable trailing columns of the variant grid — SKU and Size are the structural leading
  // columns (two-dimension handling lives in the widget), so they're excluded here.
  const variantCols = getFieldDefs(tenantConfig).filter(f =>
    f.dept === 'ops' && f.scope === 'variant' && f.key !== 'sku' && f.key !== 'size' && !disabled.includes(f.key)
  );
  const upd = (k, v) => onChange({ ...p, [k]: v, updatedAt: new Date().toISOString() });

  const generateFromPreset = (preset) => {
    const base = p.skuBase || 'SKU';
    const defaults = { price: p.basePrice || '', compareAtPrice: '', costPerItem: '', barcode: '', grams: 200, hsCode: '', coo: '' };
    // Use SKU suffix if provided, otherwise fall back to the display label
    const skuSuffix1 = (i) => (preset.opt1Skus?.[i] ?? preset.opt1Values[i]).replace(/\s+/g, '');
    const skuSuffix2 = (i) => (preset.opt2Skus?.[i] ?? preset.opt2Values[i]).replace(/\s+/g, '');
    const hasTwoOpts = preset.opt2Name && preset.opt1Values.length && preset.opt2Values.length;
    let variants;
    if (hasTwoOpts) {
      variants = [];
      preset.opt1Values.forEach((v1, i1) => {
        preset.opt2Values.forEach((v2, i2) => {
          variants.push({ id: uid(), sku: `${base}-${skuSuffix1(i1)}${skuSuffix2(i2)}`, opt1Name: preset.opt1Name, opt1Val: v1, opt2Name: preset.opt2Name, opt2Val: v2, ...defaults });
        });
      });
    } else {
      variants = preset.opt1Values.map((val, i) => ({
        id: uid(), sku: `${base}-${skuSuffix1(i)}`,
        opt1Name: preset.opt1Name || 'Size', opt1Val: val, opt2Name: '', opt2Val: '', ...defaults,
      }));
    }
    onChange({ ...p, variantType: hasTwoOpts ? TWO_DIM_VARIANT_TYPE : 'size', variants, updatedAt: new Date().toISOString() });
  };

  const updVariant = (id, field, val) =>
    upd('variants', p.variants.map(v => v.id === id ? { ...v, [field]: val } : v));

  const removeVariant = id => upd('variants', p.variants.filter(v => v.id !== id));

  const thStyle = { padding: '7px 10px', textAlign: 'left', fontWeight: 500, color: C.muted, fontSize: 11, borderBottom: `1px solid ${C.e8}`, background: C.fa, whiteSpace: 'nowrap' };
  const tdStyle = { padding: '5px 8px', borderBottom: `1px solid ${C.f4}`, verticalAlign: 'middle' };

  // Render a configurable variant-grid cell from its field definition.
  const cellInput = (def, v) => {
    const style = { ...inp({ padding: '4px 6px', fontSize: 11, ...(def.mono ? { fontFamily: F.mono } : {}) }), width: def.cellWidth || 90 };
    if (def.type === 'number' || def.type === 'price') {
      return <input style={style} type="number" step={def.type === 'price' ? '0.01' : '1'} value={v[def.key] ?? ''} onChange={e => updVariant(v.id, def.key, e.target.value)} placeholder={def.placeholder || ''} />;
    }
    return <input style={style} value={v[def.key] ?? ''} onChange={e => updVariant(v.id, def.key, e.target.value)} placeholder={def.placeholder || ''} />;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {productFields.map(f => <Field key={f.key} def={f} p={p} onChange={onChange} tenantConfig={tenantConfig} />)}
      </div>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ fontFamily: F.display, fontSize: 16, color: C.text }}>
            Variants ({p.variants.length})
          </span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {(() => {
              const productType = (p.type || '').toLowerCase();
              const visible = presets.filter(preset =>
                !preset.productTypes?.length ||
                preset.productTypes.some(t => t.toLowerCase() === productType)
              );
              return visible.length > 0
                ? visible.map(preset => (
                    <button key={preset.id} style={btn('secondary')} onClick={() => generateFromPreset(preset)}>
                      Generate {preset.label} variants
                    </button>
                  ))
                : <span style={{ fontSize: 12, color: C.muted }}>{presets.length > 0 ? 'No presets match this product type.' : 'No variant presets configured. Add them in Settings → Variant Presets.'}</span>;
            })()}
            <button style={btn('secondary')} onClick={() => {
              const defaults = { price: p.basePrice || '', compareAtPrice: '', costPerItem: '', barcode: '', grams: 200, hsCode: '', coo: '' };
              onChange({ ...p, variants: [...p.variants, { id: uid(), sku: '', opt1Name: 'Size', opt1Val: '', opt2Name: '', opt2Val: '', ...defaults }], updatedAt: new Date().toISOString() });
            }}>+ Add variant</button>
            {p.variants.length > 0 && (
              <button style={btn('secondary')} onClick={() => onChange({ ...p, variants: p.variants.map(v => ({ ...v, barcode: v.sku.replace(/-/g, '.') })), updatedAt: new Date().toISOString() })}>Generate barcodes</button>
            )}
            {p.variants.some(v => v.sku && !v.opt1Val) && (
              <button style={btn('secondary')} onClick={() => {
                // Fill each variant's size from the trailing token of its SKU (e.g. C19-BD115-M → M),
                // only where the size is still blank. Leaves already-filled sizes untouched.
                const filled = p.variants.map(v => {
                  if (!v.sku || v.opt1Val) return v;
                  const suffix = v.sku.split('-').pop();
                  return suffix ? { ...v, opt1Name: v.opt1Name || 'Size', opt1Val: suffix } : v;
                });
                onChange({ ...p, variants: filled, updatedAt: new Date().toISOString() });
              }}>Fill sizes from SKU</button>
            )}
          </div>
        </div>
        {p.variants.length > 0 && (
          <div style={{ overflowX: 'auto', borderRadius: 8, border: `1px solid ${C.e8}` }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: F.body }}>
              <thead>
                <tr>
                  <th style={thStyle}>SKU</th>
                  {isTwoDimVariant(p.variantType)
                    ? <><th style={thStyle}>{p.variants[0]?.opt1Name || 'Option 1'}</th><th style={thStyle}>{p.variants[0]?.opt2Name || 'Option 2'}</th></>
                    : <th style={thStyle}>{p.variants[0]?.opt1Name || 'Size'}</th>}
                  {variantCols.map(def => <th key={def.key} style={thStyle}>{def.label}</th>)}
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {p.variants.map(v => (
                  <tr key={v.id}>
                    <td style={tdStyle}>
                      <input style={{ ...inp({ padding: '4px 6px', fontSize: 11, fontFamily: F.mono }), width: 130 }}
                        value={v.sku} onChange={e => updVariant(v.id, 'sku', e.target.value)} />
                    </td>
                    {isTwoDimVariant(p.variantType) ? (
                      <>
                        <td style={tdStyle}><input style={{ ...inp({ padding: '4px 6px', fontSize: 11 }), width: 52 }} value={v.opt1Val} onChange={e => updVariant(v.id, 'opt1Val', e.target.value)} /></td>
                        <td style={tdStyle}><input style={{ ...inp({ padding: '4px 6px', fontSize: 11 }), width: 52 }} value={v.opt2Val} onChange={e => updVariant(v.id, 'opt2Val', e.target.value)} /></td>
                      </>
                    ) : (
                      <td style={tdStyle}><input style={{ ...inp({ padding: '4px 6px', fontSize: 11 }), width: 64 }} value={v.opt1Val} onChange={e => updVariant(v.id, 'opt1Val', e.target.value)} /></td>
                    )}
                    {variantCols.map(def => <td key={def.key} style={tdStyle}>{cellInput(def, v)}</td>)}
                    <td style={tdStyle}>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 16, padding: '0 4px', lineHeight: 1 }}
                        onClick={() => removeVariant(v.id)}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {p.variants.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: C.ph, fontSize: 13, background: C.fa, borderRadius: 8, border: `1px dashed ${C.e8}` }}>
            No variants yet. Use the generate buttons above
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TRADING FORM
// ─────────────────────────────────────────────────────────────

export function TradingForm({ p, onChange, priceTable = DEFAULT_PRICE_TABLE }) {
  const { territories, metafields, disabledFields } = useTenantConfig();
  const tableCols = getTableCols(territories);
  const disabled  = disabledFields || [];
  const upd = (k, v) => onChange({ ...p, [k]: v, updatedAt: new Date().toISOString() });

  const recalcTerritories = (price) => Object.fromEntries(
    territories.map(t => [t.code, price ? convertPrice(price, t.col, priceTable, tableCols) : ''])
  );

  const updateBasePrice = (newPrice) => {
    onChange({
      ...p,
      basePrice: newPrice,
      territoryPrices: recalcTerritories(newPrice),
      variants: (p.variants || []).map(v => ({ ...v, price: newPrice })),
      updatedAt: new Date().toISOString(),
    });
  };

  const resetToCalculated = () => {
    if (!p.basePrice) return;
    onChange({ ...p, territoryPrices: recalcTerritories(p.basePrice), updatedAt: new Date().toISOString() });
  };

  const costsWithValues = (p.variants || []).map(v => parseFloat(v.costPerItem)).filter(n => !isNaN(n) && n > 0);
  const avgCost   = costsWithValues.length ? costsWithValues.reduce((a, b) => a + b, 0) / costsWithValues.length : null;
  const sellPrice = parseFloat(p.basePrice);
  const margin    = avgCost && sellPrice > 0 ? ((sellPrice - avgCost) / sellPrice) * 100 : null;
  const marginColor = margin === null ? C.muted : margin >= 60 ? '#065f46' : margin >= 40 ? '#92400e' : '#991b1b';
  const marginBg    = margin === null ? C.fa    : margin >= 60 ? '#d1fae5'  : margin >= 40 ? '#fef3c7'  : '#fee2e2';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
        <div>
          <label style={lbl()}>Base Price (£ GBP)</label>
          <input style={inp()} type="number" step="0.01" value={p.basePrice}
            onChange={e => updateBasePrice(e.target.value)} placeholder="0.00" />
        </div>
        {!disabled.includes('compareAtPrice') && (
          <div>
            <label style={lbl()}>Compare At Price (£)</label>
            <input style={inp()} type="number" step="0.01" value={p.compareAtPrice || ''}
              onChange={e => upd('compareAtPrice', e.target.value)} placeholder="0.00" />
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <label style={lbl()}>Gross Margin</label>
          <div style={{ height: 38, display: 'flex', alignItems: 'center', paddingLeft: 12, borderRadius: 8, background: marginBg, border: `1px solid ${C.e8}`, fontFamily: F.body, fontSize: 13, color: marginColor }}>
            {margin !== null
              ? <>{margin.toFixed(1)}% <span style={{ fontSize: 12, fontWeight: 400, color: marginColor, marginLeft: 6 }}>avg cost £{avgCost.toFixed(2)}</span></>
              : <span style={{ fontSize: 13, color: C.ph }}>No cost data in Ops</span>}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <label style={lbl()}>Published on store</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38, cursor: 'pointer' }}>
            <input type="checkbox" checked={p.published === 'TRUE'}
              onChange={e => upd('published', e.target.checked ? 'TRUE' : 'FALSE')}
              style={{ width: 16, height: 16 }} />
            <span style={{ fontSize: 13, color: C.text2 }}>Visible to customers</span>
          </label>
        </div>
      </div>

      {territories.length > 0 && <div style={{ borderTop: `1px solid ${C.e8}`, paddingTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: F.display, fontSize: 16, color: C.text }}>Territory Pricing</span>
          <button style={btn('secondary')} onClick={resetToCalculated}>Reset to calculated prices</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {territories.map(t => {
            const hint = p.basePrice ? convertPrice(p.basePrice, t.col, priceTable, tableCols) : null;
            const manual = p.territoryPrices?.[t.code] || '';
            const enabled = p.territoryEnabled?.[t.code] !== false;
            return (
              <div key={t.code} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', background: enabled ? C.fa : '#f9f9f9',
                borderRadius: 8, border: `1px solid ${C.e8}`,
                opacity: enabled ? 1 : 0.55,
              }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>{t.flag}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: C.text2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</div>
                  {hint && !manual && (
                    <div style={{ fontSize: 11, color: C.ph }}>{t.symbol}{hint} suggested</div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                  <span style={{ fontSize: 11, color: C.muted }}>{t.symbol}</span>
                  <input style={{ ...inp({ width: 72, padding: '4px 6px', fontSize: 12, textAlign: 'right' }) }}
                    type="number" step="0.01" value={manual}
                    onChange={e => upd('territoryPrices', { ...p.territoryPrices, [t.code]: e.target.value })} />
                  <input type="checkbox" checked={enabled}
                    onChange={e => upd('territoryEnabled', { ...p.territoryEnabled, [t.code]: e.target.checked })}
                    title="Enable this territory"
                    style={{ width: 14, height: 14, cursor: 'pointer', flexShrink: 0 }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>}
      <MetafieldGroup fields={forDept(metafields, 'trading')} p={p} onChange={onChange} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CREATIVE FORM
// ─────────────────────────────────────────────────────────────

const toHandle = str => str.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export function CreativeForm({ p, onChange }) {
  const tenantConfig = useTenantConfig();
  const fields = deptProductFields(tenantConfig, 'creative');
  // As the title is typed, keep deriving the handle until one exists (was on the title input).
  const onFieldChange = (updated) =>
    onChange((!updated.handle && updated.title) ? { ...updated, handle: toHandle(updated.title) } : updated);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {fields.map(f => <Field key={f.key} def={f} p={p} onChange={onFieldChange} tenantConfig={tenantConfig} />)}
      </div>
      <TranslationsPanel fields={fields} p={p} onChange={onChange} translationLangs={tenantConfig.translationLangs} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// GENERIC FIELD RENDERER  (Level 2, layer 4)
// Draws a product-level field from its definition (config/fields). Built-in and custom
// fields render the same way, so a tenant's own field sits inline with the built-ins.
// ─────────────────────────────────────────────────────────────

const TEMPLATE_SOURCE = { fit: 'fitTemplate', care: 'careTemplate' };
const isFullWidth = def => def.width === 'full' || ['longtext', 'richtext', 'tags', 'image'].includes(def.type);

// A department's product-level, generically-rendered fields (built-in + custom), minus any the
// tenant has disabled. Variant-scope and widget-rendered fields (price grid, images) are excluded.
function deptProductFields(tenantConfig, dept) {
  const disabled = tenantConfig?.disabledFields || [];
  return getFieldDefs(tenantConfig).filter(f =>
    f.dept === dept && f.scope === 'product' && f.render === 'field' && !disabled.includes(f.key)
  );
}

// Chip-list tag input; quick-add suggestions come from the tenant's configured quickTags.
function TagsControl({ value, onChange, tenantConfig }) {
  const [input, setInput] = useState('');
  const tags = value || [];
  const quickTags = tenantConfig?.quickTags || [];
  const chipStyle = { display: 'inline-flex', alignItems: 'center', gap: 4, background: C.f4, border: `1px solid ${C.e8}`, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontFamily: F.body, color: C.text2 };
  const add    = (t) => { const tag = t.trim(); if (!tag || tags.includes(tag)) return; onChange([...tags, tag]); setInput(''); };
  const remove = (t) => onChange(tags.filter(x => x !== t));
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, minHeight: 24 }}>
        {tags.map(t => (
          <span key={t} style={chipStyle}>
            {t}
            <button onClick={() => remove(t)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 14, padding: 0, lineHeight: 1, display: 'flex' }}>×</button>
          </span>
        ))}
        {tags.length === 0 && <span style={{ fontSize: 12, color: C.ph }}>No tags yet</span>}
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <input style={{ ...inp(), flex: 1 }} value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(input); } }}
          placeholder="Type a tag and press Enter" />
        <button style={btn('secondary')} onClick={() => add(input)}>Add</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {quickTags.map(qt => {
          const active = tags.includes(qt);
          return (
            <button key={qt} onClick={() => active ? remove(qt) : add(qt)} style={{
              ...btn('ghost', { fontSize: 11, padding: '3px 9px', border: `1px solid ${active ? C.d0 : C.e8}`, borderRadius: 20 }),
              background: active ? C.f4 : 'transparent', color: active ? C.text : C.muted,
            }}>{qt}</button>
          );
        })}
      </div>
    </>
  );
}

// Rich HTML description field: textarea plus the description-template buttons, AI Generate, and
// an Edit/Preview toggle. Any field typed 'richtext' gets this treatment.
function RichTextField({ def, p, onChange, tenantConfig }) {
  const upd = (v) => onChange({ ...p, [def.key]: v, updatedAt: new Date().toISOString() });
  const [preview, setPreview]         = useState(false);
  const [generating, setGenerating]   = useState(false);
  const [genCooldown, setGenCooldown] = useState(false);
  const templates = tenantConfig?.descriptionTemplates || [];
  const collectionName = useContext(CollectionNameContext);

  const generate = async () => {
    if (generating || genCooldown) return;
    setGenerating(true);
    try {
      // Brand voice is matched on the collection title's brand prefix (e.g. "UNIK: …" → UNIK).
      const brandMatch = [String(collectionName || '').trim().toLowerCase()].filter(Boolean);
      const res = await apiFetch('/api/generate-description', {
        method: 'POST',
        body: JSON.stringify({
          subdomain: tenantConfig?.subdomain,
          brandMatch,
          product: {
            title:           p.title,
            titleNoColour:   p.titleNoColour || deriveTitleNoColour(p.title),
            type:            p.type,
            productCategory: p.productCategory,
            vendor:          p.vendor,
            tags:            p.tags,
            metaColor:       p.metaColor,
          },
        }),
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || 'Generation failed');
      upd(json.description);
      setGenCooldown(true);
      setTimeout(() => setGenCooldown(false), 10000);
    } catch (e) {
      alert('Description generation failed: ' + e.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={{ flex: '1 1 100%', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <label style={lbl({ marginBottom: 0 })}>{def.label}</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {templates.map(t => (
            <button key={t.id} style={btn('secondary', { fontSize: 11, padding: '4px 10px' })} onClick={() => upd(t.body)}>{t.label}</button>
          ))}
          <button
            style={btn('secondary', { fontSize: 11, padding: '4px 10px', opacity: (generating || genCooldown) ? 0.5 : 1 })}
            onClick={generate}
            disabled={generating || genCooldown}
            title={genCooldown ? 'Wait a moment before generating again' : 'Generate with AI using brand guidelines'}
          >
            {generating ? '⏳ Generating…' : genCooldown ? '✓ Generated' : '✨ Generate'}
          </button>
          <button style={btn('ghost', { fontSize: 11, padding: '4px 10px', border: `1px solid ${C.e8}`, borderRadius: 6 })}
            onClick={() => setPreview(!preview)}>{preview ? 'Edit' : 'Preview'}</button>
        </div>
      </div>
      {preview ? (
        <div style={{ border: `1px solid ${C.e8}`, borderRadius: 6, padding: 16, minHeight: 200, fontSize: 14, color: C.text, lineHeight: 1.7, background: C.fa }}
          dangerouslySetInnerHTML={{ __html: p[def.key] ? sanitizeHtml(p[def.key]) : '<em style="color:#aaa">No content yet</em>' }} />
      ) : (
        <textarea style={{ ...inp(), fontFamily: F.mono, fontSize: 12, minHeight: 220, resize: 'vertical' }}
          value={p[def.key] || ''} onChange={e => upd(e.target.value)} placeholder={def.placeholder || '<p>…</p>'} />
      )}
    </div>
  );
}

function Field({ def, p, onChange, tenantConfig }) {
  if (def.type === 'richtext') return <RichTextField def={def} p={p} onChange={onChange} tenantConfig={tenantConfig} />;
  const upd = (v) => onChange({ ...p, [def.key]: v, updatedAt: new Date().toISOString() });
  const val = p[def.key] ?? '';
  const monoStyle = def.mono ? { fontFamily: F.mono, fontSize: 12 } : {};

  const templateVal = def.template ? tenantConfig?.[TEMPLATE_SOURCE[def.template]] : null;
  const header = templateVal
    ? (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <label style={lbl({ marginBottom: 0 })}>{def.label}</label>
        <button style={btn('secondary', { fontSize: 11, padding: '4px 10px' })} onClick={() => upd(templateVal)}>Use template</button>
      </div>
    )
    : <label style={lbl()}>{def.label}</label>;

  let control;
  if (def.type === 'tags') {
    control = <TagsControl value={p[def.key]} onChange={upd} tenantConfig={tenantConfig} />;
  } else if (def.type === 'longtext') {
    control = <textarea style={{ ...inp(), fontFamily: F.mono, fontSize: 12, minHeight: 150, resize: 'vertical' }}
      value={val} onChange={e => upd(e.target.value)} placeholder={def.placeholder || `${def.label}…`} />;
  } else if (def.type === 'select') {
    control = <select style={inp()} value={val} onChange={e => upd(e.target.value)}>
      {(def.options || []).map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
    </select>;
  } else if (def.type === 'number' || def.type === 'price') {
    control = <input style={inp()} type="number" step={def.type === 'price' ? '0.01' : '1'}
      value={val} onChange={e => upd(e.target.value)} placeholder={def.placeholder || ''} />;
  } else if (def.type === 'date') {
    control = <input style={inp()} type="date" value={val} onChange={e => upd(e.target.value)} />;
  } else {
    control = <input style={{ ...inp(), ...monoStyle }} value={val} onChange={e => upd(e.target.value)} placeholder={def.placeholder || ''} />;
  }

  return <div style={{ flex: isFullWidth(def) ? '1 1 100%' : '1 1 240px', minWidth: 0 }}>{header}{control}</div>;
}

// Shared translations panel: a language switcher plus, for the active language, a translated
// input for each of the department's translatable fields.
function TranslationsPanel({ fields, p, onChange, translationLangs }) {
  const translatable = fields.filter(f => f.translatable);
  const [lang, setLang] = useState(translationLangs[0]?.code ?? null);
  if (!translatable.length || !translationLangs.length) return null;

  const updT = (langCode, key, v) => {
    const translations = { ...p.translations, [langCode]: { ...(p.translations?.[langCode] || {}), [key]: v } };
    onChange({ ...p, translations, updatedAt: new Date().toISOString() });
  };
  const filledLangs = new Set(translationLangs.filter(l => translatable.some(f => p.translations?.[l.code]?.[f.key])).map(l => l.code));
  const active  = p.translations?.[lang] || {};
  const langObj = translationLangs.find(l => l.code === lang);

  return (
    <div style={{ borderTop: `1px solid ${C.e8}`, paddingTop: 20 }}>
      <div style={{ fontFamily: F.display, fontSize: 16, color: C.text, marginBottom: 14 }}>Translations</div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        {translationLangs.map(l => {
          const isActive = lang === l.code;
          const filled   = filledLangs.has(l.code);
          return (
            <button key={l.code} onClick={() => setLang(l.code)} style={{
              ...btn(isActive ? 'primary' : 'secondary', { fontSize: 12, padding: '6px 12px', position: 'relative' }),
              background: isActive ? C.text : C.fa,
            }}>
              {l.flag} {l.code}
              {filled && !isActive && <span style={{ position: 'absolute', top: 3, right: 3, width: 6, height: 6, borderRadius: '50%', background: '#6ee7b7' }} />}
            </button>
          );
        })}
      </div>
      {langObj && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, background: C.fa, border: `1px solid ${C.e8}`, borderRadius: 8, padding: 16 }}>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>{langObj.flag} {langObj.label}, used in the {langObj.code} territory CSV export</div>
          {translatable.map(f => {
            const v = active[f.key] || '';
            const isArea = f.type === 'longtext' || f.type === 'richtext';
            return (
              <div key={f.key}>
                <label style={lbl()}>{f.label} ({langObj.code})</label>
                {isArea ? (
                  <textarea style={{ ...inp(), fontFamily: F.mono, fontSize: 12, minHeight: 130, resize: 'vertical' }}
                    value={v} onChange={e => updT(lang, f.key, e.target.value)} placeholder={`Translated ${f.label.toLowerCase()}…`} />
                ) : (
                  <input style={inp()} value={v} onChange={e => updT(lang, f.key, e.target.value)}
                    placeholder={p[f.key] ? `e.g. ${p[f.key]}` : `Translated ${f.label.toLowerCase()}…`} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// GENERIC DEPARTMENT FORM
// Renders any department that has no bespoke component — its assigned product fields plus
// translations, entirely from the field definitions. This is what custom (tenant-added)
// departments use, and it's why a new department needs no new code.
// ─────────────────────────────────────────────────────────────

export function GenericDeptForm({ p, onChange, dept }) {
  const tenantConfig = useTenantConfig();
  const fields = deptProductFields(tenantConfig, dept);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {fields.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', color: C.muted, fontSize: 13, background: C.fa, borderRadius: 8, border: `1px dashed ${C.e8}` }}>
          No fields assigned to this section yet. Add them in Settings → Fields.
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {fields.map(f => <Field key={f.key} def={f} p={p} onChange={onChange} tenantConfig={tenantConfig} />)}
      </div>
      <TranslationsPanel fields={fields} p={p} onChange={onChange} translationLangs={tenantConfig.translationLangs} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TECHNICAL FORM  (field-driven)
// ─────────────────────────────────────────────────────────────

export function TechnicalForm({ p, onChange }) {
  const tenantConfig = useTenantConfig();
  const fields = deptProductFields(tenantConfig, 'technical');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {fields.map(f => <Field key={f.key} def={f} p={p} onChange={onChange} tenantConfig={tenantConfig} />)}
      </div>
      <TranslationsPanel fields={fields} p={p} onChange={onChange} translationLangs={tenantConfig.translationLangs} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// METAFIELD GROUP — renders a tenant's custom metafields (Settings → Fields) that are
// assigned to the current department. Shared by every department form.
// ─────────────────────────────────────────────────────────────

function MetafieldGroup({ fields, p, onChange }) {
  const upd = (k, v) => onChange({ ...p, [k]: v, updatedAt: new Date().toISOString() });
  if (!fields || fields.length === 0) return null;
  return (
    <div style={{ borderTop: `1px solid ${C.e8}`, paddingTop: 16 }}>
      <div style={{ fontFamily: F.display, fontSize: 15, color: C.text, marginBottom: 14 }}>Metafields</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {fields.map(({ key, label, type }) => {
          const isArea = type === 'multi_line_text_field' || type === 'list.single_line_text_field' || type === 'longtext';
          return (
            <div key={key} style={isArea ? { gridColumn: '1 / -1' } : {}}>
              <label style={lbl()}>{label}</label>
              {isArea ? (
                <textarea style={{ ...inp(), fontFamily: F.mono, fontSize: 12, minHeight: 80, resize: 'vertical' }}
                  value={p[key] || ''} onChange={e => upd(key, e.target.value)} />
              ) : (
                <input style={{ ...inp(), fontFamily: F.mono, fontSize: 12 }} value={p[key] || ''}
                  onChange={e => upd(key, e.target.value)} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const forDept = (metafields, dept) => (metafields || []).filter(f => (f.dept || 'digital') === dept);

// ─────────────────────────────────────────────────────────────
// DIGITAL FORM
// ─────────────────────────────────────────────────────────────

export function DigitalForm({ p, onChange }) {
  const tenantConfig = useTenantConfig();
  const { badges } = tenantConfig;
  const fields = deptProductFields(tenantConfig, 'digital');
  const upd = (k, v) => onChange({ ...p, [k]: v, updatedAt: new Date().toISOString() });

  // Auto-derive handle from title for imported/existing products that have a title but no handle
  React.useEffect(() => {
    if (!p.handle && p.title) onChange({ ...p, handle: toHandle(p.title), updatedAt: new Date().toISOString() });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleBadge = (b) => {
    const curr = p.metaBadges || [];
    upd('metaBadges', curr.includes(b) ? curr.filter(x => x !== b) : [...curr, b]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {fields.map(f => <Field key={f.key} def={f} p={p} onChange={onChange} tenantConfig={tenantConfig} />)}
      </div>

      {/* Badges — a widget (toggle from the tenant's configured badge list), not a data field */}
      {badges?.length > 0 && (
        <div>
          <label style={lbl()}>Badges</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {badges.map(b => {
              const active = (p.metaBadges || []).includes(b);
              return (
                <button key={b} onClick={() => toggleBadge(b)} style={{
                  ...btn('ghost', { fontSize: 12, padding: '5px 14px', border: `1px solid ${active ? C.text : C.e8}`, borderRadius: 6 }),
                  background: active ? C.text : C.white, color: active ? C.white : C.text,
                }}>{b}</button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// IMAGE DOWNLOAD
// ─────────────────────────────────────────────────────────────

// Saves one image to the person's machine. Shown wherever imagery is visible, so anyone who
// needs the asset — creative opening a shot to retouch, most often — can take it themselves
// instead of asking studio to send it over.
// The name the file lands under. Imported imagery carries whatever the source CDN called it
// — "i3J5ypK0.jpg", a bare uuid — which tells a retoucher nothing once it's in their
// downloads folder. Rebuild it from the product and its position instead, keeping the
// original extension. Costs nothing: ?download= sets the name server-side.
function saveAsName(p, img, index) {
  const stem = (p.handle || p.title || p.skuBase || 'image')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const ext = (img.filename || img.url || '').match(/\.([a-z0-9]{2,5})(?:\?|$)/i)?.[1] || 'jpg';
  return `${stem || 'image'}-${String(index + 1).padStart(2, '0')}.${ext.toLowerCase()}`;
}

export function DownloadImageButton({ img, saveAs, label = '↓ Save', style }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState('');

  const go = async () => {
    setBusy(true); setErr('');
    try { await downloadProductImage(img, saveAs); }
    catch (e) { setErr(e.message || 'Download failed'); }
    finally { setBusy(false); }
  };

  return (
    <button onClick={go} disabled={busy}
      title={err || `Download ${saveAs || img.filename || 'image'}`}
      style={{ ...btn('ghost', { fontSize: 11, padding: '3px 8px' }), ...(err ? { color: '#c00' } : {}), ...style }}>
      {busy ? '…' : err ? 'Failed' : label}
    </button>
  );
}

// Every image of the product in one click, named `{handle}-01.jpg`, `-02`… in display order,
// so the files sort the same way in the downloads folder as they do here.
export function DownloadAllImagesButton({ p, style }) {
  const images = p.images || [];
  const [progress, setProgress] = useState(null);
  const [failed, setFailed]     = useState(0);
  if (!images.length) return null;

  const go = async () => {
    setFailed(0); setProgress(0);
    const res = await downloadProductImages(images, (img, i) => saveAsName(p, img, i), { onProgress: n => setProgress(n) });
    setFailed(res.failed); setProgress(null);
  };

  return (
    <button onClick={go} disabled={progress !== null}
      title={failed ? `${failed} image${failed === 1 ? '' : 's'} couldn't be downloaded` : 'Download every image of this product'}
      style={{ ...btn('ghost', { fontSize: 12, padding: '4px 10px' }), ...(failed ? { color: '#c00' } : {}), ...style }}>
      {progress !== null ? `Downloading ${progress}/${images.length}…`
        : failed ? `↓ Download all (${failed} failed)` : `↓ Download all (${images.length})`}
    </button>
  );
}

// What someone without the studio role sees in the Studio section: the imagery, in order,
// with a way to take it — but none of the add / reorder / remove controls.
export function StudioImagesReadOnly({ p }) {
  const images = p.images || [];
  if (!images.length) return null;
  const primaryUrl = images.find(m => !isVideoMedia(m))?.url;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 12, color: C.muted }}>View only — you can download this media but not change it.</span>
        <DownloadAllImagesButton p={p} style={{ marginLeft: 'auto' }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
        {images.map((img, i) => (
          <div key={img.url} style={{ padding: 8, background: C.fa, border: `1px solid ${C.e8}`, borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ height: 160, borderRadius: 6, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              <MediaThumb m={img} />
            </div>
            <DownloadImageButton img={img} saveAs={saveAsName(p, img, i)} label={img.url === primaryUrl ? '↓ Save (primary)' : '↓ Save'} />
          </div>
        ))}
      </div>
    </div>
  );
}

// A still or a video, fitted into whatever box it's given. Videos show their first frame
// (preload="metadata") and play muted on hover, so a grid of them stays cheap to load.
function MediaThumb({ m }) {
  const fit = { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', pointerEvents: 'none', display: 'block' };
  if (!isVideoMedia(m)) return <img src={m.url} alt={m.filename} style={fit} />;
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseEnter={e => e.currentTarget.querySelector('video')?.play().catch(() => {})}
      onMouseLeave={e => { const v = e.currentTarget.querySelector('video'); if (v) { v.pause(); v.currentTime = 0; } }}>
      <video src={m.url} muted playsInline loop preload="metadata" style={fit} />
      <span style={{ position: 'absolute', bottom: 6, left: 6, fontSize: 10, fontWeight: 700, background: 'rgba(0,0,0,0.65)', color: '#fff', borderRadius: 4, padding: '2px 6px', letterSpacing: '0.04em' }}>▶ VIDEO</span>
    </div>
  );
}

// Full-size look at one item, Shopify-style: arrows/←→ step through, Esc closes, and the
// per-item actions live here so the grid itself stays just thumbnails.
function MediaViewer({ images, index, primaryUrl, onNavigate, onClose, onMove, onMakePrimary, onCrop, onRevert, onRemove, saveAs }) {
  const img = images[index];
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onNavigate(-1);
      else if (e.key === 'ArrowRight') onNavigate(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onNavigate]);
  if (!img) return null;
  const small = { ...btn('ghost', { fontSize: 12, padding: '5px 10px' }) };
  const navBtn = (dir, disabled) => (
    <button onClick={() => onNavigate(dir)} disabled={disabled} aria-label={dir < 0 ? 'Previous' : 'Next'}
      style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 20, border: `1px solid ${C.e8}`, background: '#fff', fontSize: 18, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.3 : 1 }}>
      {dir < 0 ? '‹' : '›'}
    </button>
  );
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: 'min(1100px, 100%)', maxHeight: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: `1px solid ${C.e8}` }}>
          <span style={{ fontSize: 12, color: C.muted, flexShrink: 0 }}>{index + 1} / {images.length}</span>
          {img.url === primaryUrl && (
            <span style={{ fontSize: 10, fontWeight: 700, background: C.text, color: '#fff', borderRadius: 4, padding: '2px 6px', letterSpacing: '0.04em' }}>PRIMARY</span>
          )}
          {img.original && (
            <span style={{ fontSize: 10, fontWeight: 700, border: `1px solid ${C.e8}`, color: C.muted, borderRadius: 4, padding: '1px 6px', letterSpacing: '0.04em' }}>CROPPED{img.crop?.out ? ` ${img.crop.out.w}×${img.crop.out.h}` : ''}</span>
          )}
          <span title={img.url} style={{ fontSize: 12, color: C.muted, fontFamily: F.mono, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{img.filename}</span>
          <button onClick={onClose} aria-label="Close" style={{ marginLeft: 'auto', border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: C.muted }}>×</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, minHeight: 0, flex: 1 }}>
          {navBtn(-1, index === 0)}
          <div style={{ flex: 1, height: 'min(70vh, 760px)', background: '#f3f4f6', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {isVideoMedia(img)
              ? <video key={img.url} src={img.url} controls playsInline style={{ maxWidth: '100%', maxHeight: '100%' }} />
              : <img src={img.url} alt={img.filename} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
          </div>
          {navBtn(1, index === images.length - 1)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '12px 16px', borderTop: `1px solid ${C.e8}` }}>
          {index > 0 && !isVideoMedia(img) && <button onClick={onMakePrimary} style={small}>Make primary</button>}
          <button onClick={() => onMove(-1)} disabled={index === 0} style={{ ...small, opacity: index === 0 ? 0.3 : 1 }}>← Move</button>
          <button onClick={() => onMove(1)} disabled={index === images.length - 1} style={{ ...small, opacity: index === images.length - 1 ? 0.3 : 1 }}>Move →</button>
          <button onClick={onCrop} style={small}>{isVideoMedia(img) ? 'Crop / trim' : 'Crop'}</button>
          {img.original && <button onClick={onRevert} title="Go back to the uncropped original" style={small}>Revert</button>}
          <DownloadImageButton img={img} saveAs={saveAs} label="↓ Save" style={{ fontSize: 12, padding: '5px 10px' }} />
          <button onClick={onRemove} style={{ ...small, color: '#c00', marginLeft: 'auto' }}>Remove</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// STUDIO FORM
// ─────────────────────────────────────────────────────────────

const MEDIA_GAP = 10;
const MEDIA_MIN_TILE = 130;   // narrowest thumbnail column before the grid drops a column

// Lives in utils/products now (the CSV importer needs it too); re-exported here so the
// existing `from '../products/forms'` imports keep working.
export { syncImagesFields };

export function StudioForm({ p, onChange }) {
  const { metafields, cropPresets } = useTenantConfig();
  const [uploading, setUploading] = useState(false);
  const [cropping, setCropping]   = useState(null);   // index of the item open in CropModal
  const [cropPresetId, setCropPresetId] = useState(null); // last preset picked, kept while cycling
  const [selected, setSelected] = useState(() => new Set());  // URLs ticked for bulk remove
  React.useEffect(() => { setSelected(new Set()); }, [p.id]);
  const [dragOver, setDragOver]   = useState(null);
  const dragIdx = React.useRef(null);
  const [viewing, setViewing]     = useState(null);   // index open in MediaViewer
  const [expanded, setExpanded]   = useState(false);  // grid shows every item, not just two rows
  const [hovered, setHovered]     = useState(null);
  React.useEffect(() => { setViewing(null); setExpanded(false); }, [p.id]);
  const images = p.images || [];

  // Lay the grid out from its measured width: columns of at least MEDIA_MIN_TILE, rows sized
  // for 3:4 shots, the primary spanning 2×2 like Shopify's media grid.
  const gridRef = React.useRef(null);
  const [gridW, setGridW] = useState(0);
  const hasImages = images.length > 0;
  React.useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setGridW(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasImages]);

  const updateImages = (next) => {
    onChange({ ...p, images: next, ...syncImagesFields(next), updatedAt: new Date().toISOString() });
  };

  const primaryUrl = images.find(m => !isVideoMedia(m))?.url;

  const uploadFile = uploadMedia;

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const added = [];
      for (const file of files) {
        const img = await uploadFile(file, p.id);
        added.push(img);
      }
      updateImages([...images, ...added]);
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const moveTo = (from, to) => {
    if (from === to || to < 0 || to >= images.length) return;
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    updateImages(next);
  };

  const remove = (i) => updateImages(images.filter((_, j) => j !== i));

  const toggleSelected = (url) => setSelected(prev => {
    const n = new Set(prev); n.has(url) ? n.delete(url) : n.add(url); return n;
  });
  const liveSelected = images.filter(m => selected.has(m.url));
  const removeSelected = () => {
    const n = liveSelected.length;
    if (!n || !window.confirm(`Remove ${n} item${n === 1 ? '' : 's'} from this product?`)) return;
    updateImages(images.filter(m => !selected.has(m.url)));
    setSelected(new Set());
  };

  // A crop replaces the item in place but remembers the untouched original, so it can be
  // re-cropped from full quality or reverted. Storage has no delete, so the original file
  // is always still there to go back to.
  const applyCrop = async (i, file, crop, advance) => {
    const uploaded = await uploadFile(file, p.id);
    const next = [...images];
    next[i] = croppedItem(images[i], uploaded, crop);
    updateImages(next);
    setCropPresetId(crop.presetId);
    setCropping(advance && i + 1 < next.length ? i + 1 : null);
  };
  // Results of "Crop all", keyed by index — uploaded together, then swapped in in one update.
  const applyBulkCrop = async (results) => {
    const entries = Object.entries(results);
    const uploaded = {};
    for (let k = 0; k < entries.length; k += 4) {
      await Promise.all(entries.slice(k, k + 4).map(async ([i, { file }]) => { uploaded[i] = await uploadFile(file, p.id); }));
    }
    updateImages(images.map((m, i) => uploaded[i] ? croppedItem(m, uploaded[i], results[i].crop) : m));
  };
  const revert = (i) => {
    const next = [...images];
    next[i] = images[i].original;
    updateImages(next);
  };

  const handleDragStart = (i) => { dragIdx.current = i; };
  const handleDragOver  = (e, i) => { e.preventDefault(); setDragOver(i); };
  const handleDrop      = (e, i) => {
    e.preventDefault();
    const from = dragIdx.current;
    if (from !== null) moveTo(from, i);
    dragIdx.current = null;
    setDragOver(null);
  };
  const handleDragEnd   = () => { dragIdx.current = null; setDragOver(null); };

  const cols = gridW ? Math.max(1, Math.floor((gridW + MEDIA_GAP) / (MEDIA_MIN_TILE + MEDIA_GAP))) : 4;
  const colW = gridW ? (gridW - MEDIA_GAP * (cols - 1)) / cols : MEDIA_MIN_TILE;
  const bigPrimary = cols >= 3;
  // Collapsed, the grid keeps to two rows: the 2×2 primary plus the small tiles beside it,
  // the last two slots going to "+N more" and the add tile.
  const smallSlots = bigPrimary ? 2 * cols - 4 : Infinity;
  const collapsed = !expanded && images.length > smallSlots;
  const shownCount = collapsed ? 1 + Math.max(0, smallSlots - 2) : images.length;
  const hiddenCount = images.length - shownCount;

  const anySelected = liveSelected.length > 0;
  const renderTile = (img, i) => {
    const big = i === 0 && bigPrimary;
    const isSel = selected.has(img.url);
    const showChrome = hovered === i || isSel || anySelected;
    return (
      <div key={img.url}
        draggable
        onDragStart={() => handleDragStart(i)}
        onDragOver={e => handleDragOver(e, i)}
        onDrop={e => handleDrop(e, i)}
        onDragEnd={handleDragEnd}
        onClick={() => setViewing(i)}
        onMouseEnter={() => setHovered(i)}
        onMouseLeave={() => setHovered(h => (h === i ? null : h))}
        title={img.filename}
        style={{ position: 'relative', gridColumn: big ? 'span 2' : undefined, gridRow: big ? 'span 2' : undefined, borderRadius: 10, overflow: 'hidden', background: '#f3f4f6', cursor: 'grab', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `${dragOver === i || isSel ? 2 : 1}px solid ${dragOver === i ? C.text : isSel ? '#3b82f6' : C.e8}`, opacity: dragIdx.current === i ? 0.4 : 1, transition: 'border-color 0.15s' }}>
        <MediaThumb m={img} />
        {showChrome && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.08)', pointerEvents: 'none' }} />}
        {showChrome && (
          <input type="checkbox" checked={isSel}
            onClick={e => e.stopPropagation()} onChange={() => toggleSelected(img.url)}
            title="Select to remove several at once"
            style={{ position: 'absolute', top: 8, left: 8, width: 16, height: 16, cursor: 'pointer' }} />
        )}
        <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 4, pointerEvents: 'none' }}>
          {img.url === primaryUrl && !big && (
            <span style={{ fontSize: 9, fontWeight: 700, background: C.text, color: '#fff', borderRadius: 4, padding: '2px 5px', letterSpacing: '0.04em' }}>PRIMARY</span>
          )}
          {img.original && (
            <span style={{ fontSize: 9, fontWeight: 700, background: 'rgba(255,255,255,0.9)', color: C.muted, borderRadius: 4, padding: '2px 5px', letterSpacing: '0.04em' }}>CROPPED</span>
          )}
        </div>
      </div>
    );
  };
  const tileBox = { borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 13 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ ...btn('secondary'), cursor: 'pointer', display: 'inline-block' }}>
          {uploading ? 'Uploading…' : '+ Add images or video'}
          <input type="file" accept="image/*,video/mp4,video/quicktime,video/webm,.mov,.m4v" multiple style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
        </label>
        <span style={{ fontSize: 12, color: C.muted }}>First image is the primary (a video can't be). Drag to reorder, click to open.</span>
        {images.length > 1 && (
          <button onClick={() => updateImages([...images].sort((a, b) => a.filename.localeCompare(b.filename, undefined, { numeric: true, sensitivity: 'base' })))}
            style={{ ...btn('ghost', { fontSize: 12, padding: '4px 10px' }) }}>Sort by filename</button>
        )}
        <DownloadAllImagesButton p={p} />
        <BulkCrop groups={[{ id: p.id, label: p.title || p.handle, images }]} presets={cropPresets || []} onGroupDone={(_, results) => applyBulkCrop(results)} />
      </div>

      {liveSelected.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 12, position: 'sticky', top: 0, zIndex: 2 }}>
          <span style={{ fontWeight: 600, color: C.text }}>{liveSelected.length} selected</span>
          {liveSelected.length < images.length && (
            <button onClick={() => setSelected(new Set(images.map(m => m.url)))} style={btn('ghost', { fontSize: 12, padding: '3px 8px' })}>Select all</button>
          )}
          <button onClick={() => setSelected(new Set())} style={btn('ghost', { fontSize: 12, padding: '3px 8px' })}>Clear</button>
          <button onClick={removeSelected} style={{ ...btn('ghost', { fontSize: 12, padding: '3px 10px' }), color: '#c00', marginLeft: 'auto' }}>Remove selected</button>
        </div>
      )}

      {images.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: C.muted, fontSize: 13, background: C.fa, border: `1px solid ${C.e8}`, borderRadius: 10 }}>
          No images yet. Upload images or video, or assign from a collection folder.
        </div>
      ) : (
        <div>
          <div ref={gridRef}
            style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridAutoRows: Math.round(colW * 4 / 3), gap: MEDIA_GAP }}>
            {images.slice(0, shownCount).map(renderTile)}
            {collapsed && hiddenCount > 0 && (
              // Dragging over it opens the full grid so an item can be dropped anywhere.
              <div onClick={() => setExpanded(true)} onDragOver={e => { e.preventDefault(); setExpanded(true); }}
                title="Show all"
                style={{ ...tileBox, position: 'relative', overflow: 'hidden', background: '#f3f4f6', border: `1px solid ${C.e8}` }}>
                <div style={{ position: 'absolute', inset: 0, filter: 'blur(6px)', opacity: 0.6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <MediaThumb m={images[shownCount]} />
                </div>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} />
                <span style={{ position: 'relative', color: '#fff', fontSize: 20, fontWeight: 600 }}>+{hiddenCount}</span>
              </div>
            )}
            <label title="Add images or video"
              style={{ ...tileBox, border: `1.5px dashed ${C.e8}`, background: C.fa, color: C.muted, fontSize: uploading ? 12 : 26 }}>
              {uploading ? 'Uploading…' : '+'}
              <input type="file" accept="image/*,video/mp4,video/quicktime,video/webm,.mov,.m4v" multiple style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
            </label>
          </div>
          {expanded && images.length > smallSlots && (
            <button onClick={() => setExpanded(false)} style={{ ...btn('ghost', { fontSize: 12, padding: '4px 10px' }), marginTop: 8 }}>Show less</button>
          )}
        </div>
      )}
      {viewing !== null && images[viewing] && (
        <MediaViewer images={images} index={viewing} primaryUrl={primaryUrl}
          onNavigate={dir => setViewing(i => Math.min(images.length - 1, Math.max(0, i + dir)))}
          onClose={() => setViewing(null)}
          onMove={dir => { moveTo(viewing, viewing + dir); setViewing(viewing + dir); }}
          onMakePrimary={() => { moveTo(viewing, 0); setViewing(0); }}
          onCrop={() => { setCropping(viewing); setViewing(null); }}
          onRevert={() => revert(viewing)}
          onRemove={() => {
            if (!window.confirm('Remove this item from the product?')) return;
            remove(viewing);
            setViewing(images.length > 1 ? Math.min(viewing, images.length - 2) : null);
          }}
          saveAs={saveAsName(p, images[viewing], viewing)} />
      )}
      {cropping !== null && images[cropping] && (
        <CropModal key={cropping} media={images[cropping]} presets={cropPresets || []}
          index={cropping} total={images.length}
          onNavigate={dir => setCropping(i => Math.min(images.length - 1, Math.max(0, i + dir)))}
          presetId={cropPresetId} onPresetChange={setCropPresetId}
          productId={p.id} productLabel={p.title || p.handle}
          onApply={(file, crop, advance) => applyCrop(cropping, file, crop, advance)}
          onClose={() => setCropping(null)} />
      )}
      <MetafieldGroup fields={forDept(metafields, 'studio')} p={p} onChange={onChange} />
    </div>
  );
}
